/**
 * Visualizer — the render layer.
 *
 * It is a pure paramBus *consumer*. It subscribes to the bus and mirrors the
 * `fx.*` params into shader uniforms; it never sees a KeyboardEvent and never
 * imports the input layer. Swapping how a key behaves, or amplifying it, changes
 * only the uniform value that arrives here — never this file.
 *
 * The uniforms object is built in the constructor (independent of WebGL), so the
 * input→uniform wiring is observable even where a GPU/WebGL context isn't
 * available; `mount()` then best-effort spins up the renderer + animation loop.
 *
 * Two clocks live here rather than on the bus, because they advance per-frame and
 * pushing them through the bus would flood it: `virtualTime` (the animation clock,
 * integrated from real dt scaled by global speed) and the beat phase (derived from
 * the bus's slow tempo values). They're exposed read/sync so a second-display
 * mirror can match them — see `mirror/mirrorLink.ts`.
 */

import * as THREE from 'three';
import { ParamBus } from '../state/paramBus';
import { effectConfig } from '../effects/effectConfig';
import { visualConfig, visualModParam } from '../effects/visualConfig';
import { comboConfig } from '../effects/comboConfig';
import { vertexShader, fragmentShader } from './shaders';

/** Effect id -> `u<Capitalized>` shader uniform (matches the GLSL declarations). */
function effectUniformName(effect: string): string {
  return 'u' + effect.charAt(0).toUpperCase() + effect.slice(1);
}

/** Effect id -> `uMod<Capitalized>` modifier uniform (matches the GLSL). */
function modUniformName(effect: string): string {
  return 'uMod' + effect.charAt(0).toUpperCase() + effect.slice(1);
}

/** Base-visual id -> `uVMod<Capitalized>` modifier uniform (matches the GLSL). */
function vmodUniformName(id: string): string {
  return 'uVMod' + id.charAt(0).toUpperCase() + id.slice(1);
}

/** Every letter effect is wired to the shader this phase. */
export const WIRED_EFFECTS: ReadonlySet<string> = new Set(effectConfig.map((d) => d.effect));

/** Neutral modifier value — 0.5 means "current look" for every effect. */
const MODIFIER_DEFAULT = 0.5;

/** Combo id -> `uCombo<Capitalized>` shader uniform (matches the GLSL). */
function comboUniformName(name: string): string {
  return 'uCombo' + name.charAt(0).toUpperCase() + name.slice(1);
}

/** paramBus key -> shader uniform name (combos). */
const COMBO_UNIFORM: Readonly<Record<string, string>> = Object.fromEntries(
  comboConfig.map((c) => [`combo.${c.name}`, comboUniformName(c.name)]),
);

/** paramBus key -> shader uniform name (effects + modifiers + combos + selectors). */
const PARAM_UNIFORM: Readonly<Record<string, string>> = {
  ...Object.fromEntries(effectConfig.map((d) => [`fx.${d.effect}`, effectUniformName(d.effect)])),
  ...Object.fromEntries(effectConfig.map((d) => [`mod.${d.effect}`, modUniformName(d.effect)])),
  ...Object.fromEntries(visualConfig.map((v) => [visualModParam(v.id), vmodUniformName(v.id)])),
  ...COMBO_UNIFORM,
  'visual.select': 'uVisual',    // scalar mirror: active base-visual index
  'palette.type': 'uPaletteType', // scalar mirror: active palette-type index
  'palette.hue': 'uBaseHue',      // scalar mirror: active type's base hue (0..1)
  'global.zoom': 'uGlobalZoom',   // scalar mirror: persistent camera zoom (0.5 = 1x)
  // global.speed is NOT a uniform — it feeds the virtual-time integrator (below).
};

/**
 * Map global.speed (0..1) to a signed time multiplier, symmetric around the 0.5
 * midpoint: below 0.5 runs in reverse, above runs forward, and the two ends are
 * equal-and-opposite. 0 -> -3x, 0.5 -> 0x (a full freeze), 1 -> +3x. The slider
 * defaults to GLOBAL_SPEED_DEFAULT (2/3), where this evaluates to +1x — so the
 * app opens at normal forward speed with headroom to speed up or reverse.
 */
export function mapSpeed(s: number): number {
  return (s - 0.5) * 6;
}

/** BPM used before any taps exist, so Strobe still has a beat to flash on. */
const DEFAULT_BPM = 120;

/**
 * Fraction of an outstanding clock correction absorbed per frame. Small enough
 * that a mirror easing onto the controller's virtual time is invisible (~10
 * frames), large enough to converge before the next 4Hz heartbeat arrives.
 */
const DRIFT_CATCHUP = 0.15;

/** Past this much drift, jump instead of easing (fresh mirror, or a throttled tab). */
const DRIFT_SNAP_S = 1;

/** Below this, absorb the rest — stops a correction trailing a residual forever. */
const DRIFT_EPSILON = 1e-4;

/**
 * Advance the virtual clock one frame, absorbing any outstanding correction.
 *
 * Split out as a pure function because the render loop needs a GPU but this
 * doesn't — it's where the second-display sync either works or visibly doesn't.
 * `drift` is an offset to bleed off, independent of the ongoing integration:
 * both ends integrate the same `timeScale` (mirrored via `global.speed`), so a
 * measured offset stays valid while it's being applied.
 */
export function nextVirtualTime(
  current: number,
  dt: number,
  timeScale: number,
  drift: number,
): { time: number; drift: number } {
  const advanced = current + dt * timeScale;
  if (Math.abs(drift) > DRIFT_SNAP_S || Math.abs(drift) < DRIFT_EPSILON) {
    return { time: advanced + drift, drift: 0 };
  }
  const step = drift * DRIFT_CATCHUP;
  return { time: advanced + step, drift: drift - step };
}

export class Visualizer {
  readonly uniforms: Record<string, THREE.IUniform>;
  private renderer?: THREE.WebGLRenderer;
  private scene?: THREE.Scene;
  private camera: THREE.Camera;
  private raf = 0;
  private readonly startedAt: number;
  private readonly unsubscribe: () => void;
  private disposed = false;

  // Tempo state, mirrored from the bus; the beat phase is derived per-frame.
  private tempoBpm = 0;
  private tempoAnchor = 0;

  // Global-speed virtual clock: uTime accumulates dt * timeScale so a *changing*
  // speed (or reverse) never jumps the animation. The beat clock stays on real time.
  private timeScale = 1;
  private virtualClock = 0;
  private lastFrameMs = -1;
  // Outstanding correction toward a controller page's clock (second-display
  // mirror only; always 0 when nothing calls syncVirtualTime).
  private virtualDrift = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    bus: ParamBus,
  ) {
    this.startedAt = now();
    this.camera = new THREE.Camera(); // vertex shader writes clip space directly

    this.uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uBeat: { value: 0 }, // 0..1 beat envelope, computed each frame
      uLive: { value: bus.get('tempo.live', 0) },
      uVisual: { value: bus.get('visual.select', 0) }, // active base-visual index
      uPaletteType: { value: bus.get('palette.type', 2) }, // active palette-type index (Spectrum)
      uBaseHue: { value: bus.get('palette.hue', 0) },      // active type's base hue
      uGlobalZoom: { value: bus.get('global.zoom', 0.5) }, // persistent camera zoom (0.5 = 1x)
    };
    // One on/off uniform + one modifier uniform per effect, seeded from the bus.
    for (const def of effectConfig) {
      this.uniforms[effectUniformName(def.effect)] = { value: bus.get(`fx.${def.effect}`, 0) };
      this.uniforms[modUniformName(def.effect)] = {
        value: bus.get(`mod.${def.effect}`, MODIFIER_DEFAULT),
      };
    }
    // One modifier uniform per base visual, seeded from the bus (0.5 neutral).
    for (const v of visualConfig) {
      this.uniforms[vmodUniformName(v.id)] = {
        value: bus.get(visualModParam(v.id), MODIFIER_DEFAULT),
      };
    }
    // Combo uniforms, seeded from the bus (0 unless a combo is already firing).
    for (const [param, name] of Object.entries(COMBO_UNIFORM)) {
      this.uniforms[name] = { value: bus.get(param, 0) };
    }

    this.tempoBpm = bus.get('tempo.bpm', 0);
    this.tempoAnchor = bus.get('tempo.anchorMs', 0);
    // Fallback matches GLOBAL_SPEED_DEFAULT (2/3 -> +1x); in practice main.ts seeds
    // the bus before this runs, so this default is only a safety net.
    this.timeScale = mapSpeed(bus.get('global.speed', 2 / 3));

    // The single read seam: bus change -> uniform / tempo state. Nothing else
    // drives the scene. The continuous beat phase is derived locally (below)
    // rather than pushed through the bus, so per-frame math never spams it.
    this.unsubscribe = bus.subscribe((change) => {
      const name = PARAM_UNIFORM[change.key];
      if (name && this.uniforms[name]) {
        this.uniforms[name].value = change.value;
      } else if (change.key === 'tempo.bpm') {
        this.tempoBpm = change.value;
      } else if (change.key === 'tempo.anchorMs') {
        this.tempoAnchor = change.value;
      } else if (change.key === 'tempo.live') {
        this.uniforms.uLive.value = change.value;
      } else if (change.key === 'global.speed') {
        this.timeScale = mapSpeed(change.value);
      }
    });
  }

  /** Create the renderer and start the loop. Returns false if WebGL is absent. */
  mount(): boolean {
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        // Opt-in only (headless capture / pixel-readback verification). Off in prod.
        preserveDrawingBuffer: (globalThis as { __glyphPreserve?: boolean }).__glyphPreserve === true,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[glyph] WebGL unavailable — uniforms still wired, scene skipped.', err);
      return false;
    }
    this.renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio || 1, 2));

    this.scene = new THREE.Scene();
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({ vertexShader, fragmentShader, uniforms: this.uniforms }),
    );
    this.scene.add(mesh);

    this.resize();
    window.addEventListener('resize', this.resize);
    this.loop();
    return true;
  }

  /**
   * The virtual animation clock (`uTime`). The only visual state not on the bus,
   * so a second-display mirror has to be told it — see `mirror/mirrorLink.ts`.
   */
  get virtualTime(): number {
    return this.virtualClock;
  }

  /**
   * The anchor `beatEnvelope()` is actually using, in local `performance.now()` ms.
   *
   * 0 is the "no tempo set yet" sentinel — *not* "non-positive". A mirror page
   * that opened after the tap gets a legitimately **negative** anchor (the beat
   * predates its own time origin), and rejecting that would silently drop it back
   * to its own start time and flash off-beat. The modulo in `beatEnvelope` handles
   * negatives fine.
   */
  get beatAnchor(): number {
    return this.tempoAnchor !== 0 ? this.tempoAnchor : this.startedAt;
  }

  /**
   * Adopt another page's virtual time (second-display mirror). Recorded as a
   * correction and eased in by `nextVirtualTime` rather than assigned, so a
   * heartbeat never jolts the animation.
   */
  syncVirtualTime(vt: number): void {
    this.virtualDrift = vt - this.virtualClock;
  }

  private loop = (): void => {
    if (this.disposed || !this.renderer || !this.scene) return;
    const t = now();
    // Virtual clock: integrate real dt scaled by global speed, so scrubbing speed
    // (or reversing) never jumps the animation. Beat stays on real wall-time.
    const dt = this.lastFrameMs < 0 ? 0 : (t - this.lastFrameMs) / 1000;
    this.lastFrameMs = t;
    const stepped = nextVirtualTime(this.virtualClock, dt, this.timeScale, this.virtualDrift);
    this.virtualClock = stepped.time;
    this.virtualDrift = stepped.drift;
    this.uniforms.uTime.value = this.virtualClock;
    this.uniforms.uBeat.value = this.beatEnvelope(t);
    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(this.loop);
  };

  /** 0..1 envelope that spikes at each downbeat and decays before the next. */
  private beatEnvelope(t: number): number {
    const bpm = this.tempoBpm > 0 ? this.tempoBpm : DEFAULT_BPM;
    const anchor = this.beatAnchor;
    const beatMs = 60000 / bpm;
    const phase = (((t - anchor) % beatMs) + beatMs) % beatMs / beatMs; // 0..1
    return (1 - phase) ** 3;
  }

  private resize = (): void => {
    if (!this.renderer) return;
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    (this.uniforms.uResolution.value as THREE.Vector2).set(w, h);
  };

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.resize);
    this.unsubscribe();
    this.renderer?.dispose();
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
